import { LossByPercentageWorkerProps } from 'features/dataLayers/generation/coreLayers/wastage/calculateLossByPercentageData.worker'
import { FdeCdfSpecification } from 'features/dataLayers/generation/fdeCdfSpecification'
import { EmatCombinedCategoryWorkerProps } from 'features/dataLayers/generation/workers/calculateEmatCombinedCategoryData.worker'
import { LossWorkerProps } from 'features/dataLayers/generation/workers/calculateLossData.worker'
import { LossPerYearWorkerProps } from 'features/dataLayers/generation/workers/calculateLossPerYearData.worker'
import { RcaWorkerProps } from 'features/dataLayers/generation/workers/calculateRcaData.worker'
import { RemainingLifeWorkerProps } from 'features/dataLayers/generation/workers/calculateRemainingLifeData.worker'
import { ThicknessByMeanWorkerProps } from 'features/dataLayers/generation/workers/calculateThicknessByMeanData.worker'
import { TimeSinceLastRepairWorkerProps } from 'features/dataLayers/generation/workers/calculateTimeSinceLastRepairData.worker'
import { TminWorkerProps } from 'features/dataLayers/generation/workers/calculateTminData.worker'
import { useCdfRawValues2DArray } from 'features/dataLayers/load/useCdfRawValues2DArray'
import { getInspectionDate } from 'features/fde'
import { isEqual, isNil } from 'lodash'
import { useCallback, useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import {
  CustomCorrosionRegion,
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoMeasurementUnit,
  GeckoView,
  isInspectionThicknessCDF,
  OverrideRegion,
} from 'types'
import { DATE_FORMAT } from 'utils/fde/constants'

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * A hook that generates the data for a derived CDF -- based on the `cdfSpecification` -- in a Web Worker.
 *
 * Note: all parameters (including the pieces of `overrides`) should be memoized, as this functions performs heavy calculations.
 */
export function useGenerateDerivedDataInWebWorker<RETURN = number>(
  cdfSpecification: FdeCdfSpecification,
  assetModel: GeckoAssetModel | undefined,
  baseThicknessCDF: GeckoComponentDataFrame | undefined,
  selectedInspection: GeckoInspection | undefined,
  forecastComponentConfig: ForecastComponentConfig | undefined,
  overrides: ParamOverrides = {},
  keepPreviousData: boolean = false
): {
  array: RETURN[][]
  isLoading: boolean
} {
  const componentId = baseThicknessCDF?.tags.component

  const { baseThicknessValuesAreLoading, calculateDataInWebWorkerFn } =
    useGetGenerateDerivedValuesInWebWorkerFn<RETURN>(
      cdfSpecification,
      componentId,
      assetModel,
      baseThicknessCDF,
      selectedInspection,
      forecastComponentConfig,
      overrides
    )

  // ------------------------------------------ Call Calculate Function ------------------------------------------

  const canCalculateData =
    cdfSpecification.canCreateCdfData(
      assetModel,
      componentId ?? '',
      forecastComponentConfig ?? {},
      selectedInspection
    ) && !isNil(calculateDataInWebWorkerFn)

  const assetModelJson = assetModel?.raw

  const { data, isLoading: rlCalcIsLoading } = useSWRImmutable(
    // key
    [
      cdfSpecification.source.id,
      cdfSpecification.view.id,
      cdfSpecification.tminMethod.id,
      baseThicknessCDF?.data.params,
      baseThicknessCDF?.generation,
      forecastComponentConfig,
      baseThicknessValuesAreLoading,
      overrides.appliedRegions,
      overrides.customCorrosionRegions,
      assetModelJson,
    ],
    // function
    async () => (canCalculateData ? calculateDataInWebWorkerFn() : undefined),
    // options
    {
      // There is no cheap way to compare CDF data to know if it has changed,
      // and SWR will attempt to use stableHash... which involves iterating over
      // all elements in the returned array. Very slow. For more information,
      // see: https://github.com/vercel/swr/issues/1719
      //
      // Instead, check if the params to the RL calculation have changed.
      compare: (a, b) => isEqual(a?.calcParams, b?.calcParams),
      keepPreviousData,
    }
  )

  // ------------------------------------------ Return ------------------------------------------

  const staticDefaultReturn: RETURN[][] = useMemo(() => [], [])

  return {
    array: data?.array ?? staticDefaultReturn,
    isLoading: rlCalcIsLoading || baseThicknessValuesAreLoading,
  }
}

// ------------------------------------------ Helper Functions ------------------------------------------

export function useGetGenerateDerivedValuesInWebWorkerFn<RETURN = number>(
  cdfSpecification: FdeCdfSpecification,
  componentId: string | undefined,
  assetModel: GeckoAssetModel | undefined,
  baseThicknessCDF: GeckoComponentDataFrame | undefined,
  selectedInspection: GeckoInspection | undefined,
  forecastComponentConfig: ForecastComponentConfig | undefined,
  overrides: ParamOverrides = {}
): {
  baseThicknessValuesAreLoading: boolean
  calculateDataInWebWorkerFn:
    | (() => Promise<{
        calcParams: DerivedCdfWorkerCalculationParams
        array: RETURN[][]
      }>)
    | undefined
} {
  // Ensure that the baseThicknessCDF is a thickness CDF, except for EmatCombinedCategory which can use EMAT CDFs
  const isValidBaseCdf =
    isNil(baseThicknessCDF) ||
    isInspectionThicknessCDF(baseThicknessCDF) ||
    (cdfSpecification === FdeCdfSpecification.EmatCombinedCategory &&
      baseThicknessCDF.data?.params?.source?.id === 'emat')

  if (!isValidBaseCdf) {
    throw new Error(
      `Invalid base thickness CDF used in remaining life CDF generation: ${baseThicknessCDF.id}`
    )
  }

  const shouldFetchBaseData = overrides.thicknessValues2dArray
    ? false
    : cdfSpecification.canCreateCdfData(
        assetModel,
        componentId ?? '',
        forecastComponentConfig ?? {},
        selectedInspection
      )

  const assetModelJson = assetModel?.raw

  // ------------------------------------ Fetch Raw Thickness Values ------------------------------------
  // Get the raw thickness values
  // if overrides.thicknessValues2d array is provided, don't fetch the 2d array
  const {
    array: baseThicknessValues = overrides.thicknessValues2dArray,
    isLoading: baseThicknessValuesAreLoading = false,
  } = useCdfRawValues2DArray(shouldFetchBaseData ? baseThicknessCDF : undefined)

  // ------------------------------------------ Web Workers ------------------------------------------

  // A function that initializes and calls a Web-Worker to calculate the remaining life values
  const calculateDataInWebWorker = useCallback(
    () =>
      Promise.resolve(
        new Promise<{
          calcParams: DerivedCdfWorkerCalculationParams
          array: RETURN[][]
        }>((resolve, reject) => {
          try {
            // Assert we've got a valid base thickness CDF
            if (isNil(baseThicknessCDF)) {
              reject(
                "Can't calculate remaining life without a base thickness CDF"
              )
              return
            }

            // Assert that the API calls we depend on have resolved
            if (baseThicknessValuesAreLoading) {
              reject(
                "Can't calculate remaining life while base thickness values are loading"
              )
              return
            }

            const inspectionDate = getInspectionDate({
              inspectionSlug: baseThicknessCDF.tags.inspection,
              inspection: selectedInspection,
            })
            const inspectionDateString = inspectionDate
              ? inspectionDate.format(DATE_FORMAT)
              : undefined

            // Assert that the API calls returned valid data
            if (
              isNil(componentId) ||
              isNil(baseThicknessValues) ||
              isNil(baseThicknessCDF?.tags.inspection) ||
              isNil(baseThicknessCDF?.tags.component) ||
              isNil(inspectionDateString) ||
              isNil(assetModelJson)
            ) {
              reject("Can't calculate remaining life without all required data")
              return
            }

            // Assemble the values needed for the remaining life calc
            // -- CDF data values
            // baseThicknessValues :check
            const binWidthJson = baseThicknessCDF.data.binning.bin_size.width
              .convertTo(GeckoMeasurementUnit.Feet)
              .toJSON()
            const binHeightJson = baseThicknessCDF.data.binning.bin_size.height
              .convertTo(GeckoMeasurementUnit.Feet)
              .toJSON()
            const xGeometryStartJson = baseThicknessCDF.data.geometry.start.x
              .convertTo(GeckoMeasurementUnit.Feet)
              .toJSON()
            const yGeometryStartJson = baseThicknessCDF.data.geometry.start.y
              .convertTo(GeckoMeasurementUnit.Feet)
              .toJSON()

            // - - - - - - - - - - - - - - - - - Initialize the Web Worker - - - - - - - - - - - - - - - - -
            // Use a web worker to calculate the data, as this calc can be
            // quite time-intensive, and we don't want to block the main thread

            // Create the web worker, passing in the path to the worker file
            // Note: Defining the web-worker using `new URL(filePath, import.meta.url), {type: 'module'}` is necessary
            //  as it allows us to import/export from within the worker-file.
            // Note: Only JSON-serializable data can be passed to/from the worker.
            const worker = cdfSpecification.getWorkerFile()

            const calcParams: DerivedCdfWorkerCalculationParams = {
              componentId,
              thicknessValues: baseThicknessValues,
              thicknessUnit: baseThicknessCDF.data.params.measurement_unit.id,
              assetModelJson,
              inspectionDateString,
              binHeightJson,
              binWidthJson,
              yGeometryStartJson,
              xGeometryStartJson,
              forecastComponentConfig: forecastComponentConfig,
              tminMethod: cdfSpecification.tminMethod.id,
              repairRegions: overrides.appliedRegions,
              corrosionAugmentationRegions: overrides.customCorrosionRegions,
              useCustomCR:
                cdfSpecification.view.id === GeckoView.CustomCRRemainingLife.id,
            }

            // The function that will be called when the web-worker posts a message
            worker.onmessage = async (message) => {
              worker.terminate()
              resolve({
                calcParams: calcParams,
                array: message.data,
              })
            }

            // The function that will be called when the web-worker posts an error
            worker.onerror = async (error) => {
              console.error(error)
              worker.terminate()
              reject(`Couldn't calculate derived CDF, ${error.message}`)
            }

            // - - - - - - - - - - - - - - - - - - Call the Web Worker - - - - - - - - - - - - - - - - - -
            // Calls the web-worker, evoking the onmessage function defined in the worker file.
            worker.postMessage(calcParams)
          } catch (error) {
            reject(`Couldn't calculate derived CDF, ${error}`)
            console.error(error)
          }
        })
      ),
    [
      baseThicknessCDF,
      baseThicknessValuesAreLoading,
      componentId,
      baseThicknessValues,
      forecastComponentConfig,
      assetModelJson,
      cdfSpecification,
      overrides.appliedRegions,
      overrides.customCorrosionRegions,
      selectedInspection,
    ]
  )
  return {
    baseThicknessValuesAreLoading,
    calculateDataInWebWorkerFn: baseThicknessValuesAreLoading
      ? undefined
      : calculateDataInWebWorker,
  }
}

// ========================================== Types ==========================================

export type ParamOverrides = {
  customCorrosionRegions?: CustomCorrosionRegion[]
  appliedRegions?: OverrideRegion[]
  thicknessValues2dArray?: number[][]
}

export type DerivedCdfWorkerCalculationParams =
  | LossWorkerProps
  | LossByPercentageWorkerProps
  | LossPerYearWorkerProps
  | TminWorkerProps
  | RcaWorkerProps
  | RemainingLifeWorkerProps
  | TimeSinceLastRepairWorkerProps
  | ThicknessByMeanWorkerProps
  | EmatCombinedCategoryWorkerProps
